import { useEffect } from 'react';
import { getSeoForPath, siteConfig } from '../../config/site';
import { homeFaqs } from '../../data/faqs';
import { serviceDetails } from '../../data/serviceDetails';

function setMetaAttribute(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function setLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function setStructuredData(data) {
  let element = document.getElementById('structured-data');

  if (!element) {
    element = document.createElement('script');
    element.id = 'structured-data';
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

const faqByPath = {
  '/': homeFaqs,
  '/services/hydraulics': serviceDetails.hydraulics.faq,
  '/services/gearboxes': serviceDetails.gearboxes.faq,
  '/services/industrial-repair': serviceDetails.industrialRepair.faq,
};

function buildFaqSchema(faqs, canonicalUrl) {
  if (!faqs || faqs.length === 0) {
    return null;
  }

  return {
    '@type': 'FAQPage',
    '@id': `${canonicalUrl}#faq`,
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function SEO({ pathname }) {
  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const title = `${seo.title} | ${siteConfig.name}`;
    const canonicalUrl = `${siteConfig.url}${seo.path === '/' ? '/' : seo.path}`;
    const imageUrl = `${siteConfig.url}/images/logos/logo.png`;
    const faqSchema = buildFaqSchema(faqByPath[seo.path], canonicalUrl);
    const structuredGraph = [
      {
        '@type': 'LocalBusiness',
        '@id': `${siteConfig.url}/#business`,
        name: siteConfig.name,
        url: siteConfig.url,
        telephone: siteConfig.phone,
        email: siteConfig.email,
        image: imageUrl,
        areaServed: {
          '@type': 'Country',
          name: siteConfig.serviceArea,
        },
        makesOffer: [
          'Industrial gearbox repair',
          'Hydraulic cylinder repair',
          'Industrial mechanical repair',
        ].map((name) => ({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name,
          },
        })),
      },
    ];

    if (faqSchema) {
      structuredGraph.push(faqSchema);
    }

    document.title = title;
    setMetaAttribute('name', 'description', seo.description);
    setMetaAttribute('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow');
    setMetaAttribute('property', 'og:type', 'website');
    setMetaAttribute('property', 'og:site_name', siteConfig.name);
    setMetaAttribute('property', 'og:title', title);
    setMetaAttribute('property', 'og:description', seo.description);
    setMetaAttribute('property', 'og:url', canonicalUrl);
    setMetaAttribute('property', 'og:image', imageUrl);
    setMetaAttribute('name', 'twitter:card', 'summary_large_image');
    setMetaAttribute('name', 'twitter:title', title);
    setMetaAttribute('name', 'twitter:description', seo.description);
    setMetaAttribute('name', 'twitter:image', imageUrl);
    setLink('canonical', canonicalUrl);

    setStructuredData({
      '@context': 'https://schema.org',
      '@graph': structuredGraph,
    });
  }, [pathname]);

  return null;
}

export default SEO;
